<template>
  <div>
    <button
      type="button"
      aria-label="Add new project"
      class="flex items-center gap-2 brand-button"
      @click="$modal.show('create-project')"
    >
      Add New Project
      <PlusSmall />
    </button>

    <ModalComponent
      name="create-project"
      :shift-x="0.97"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="true"
    >
      <section class="p-5 border-b">
        <div class="flex items-center justify-between">
          <h3 class="text-lg text-gray-900 lg:text-xl">
            Add New Project
          </h3>
          <button
            type="button"
            class="p-1 text-gray-400 bg-gray-200 rounded-md"
            @click="$modal.hide('create-project')"
          >
            <TimesSmall />
          </button>
        </div>
        <p>Please fill all fields to create your new abatement project</p>
      </section>
      <!-- Form -->
      <form
        class="p-5 mt-1"
        @submit.prevent="createProject"
      >
        <div class="flex flex-col gap-1.5">
          <label
            for="name"
            class="text-sm font-semibold"
          >Abatement Project Name</label>
          <input
            v-model="project.name.text"
            type="text"
            placeholder="Please enter your abatement project name"
            class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300 placeholder:italic"
            @keyup="validateName"
          >
          <span class="text-xs font-semibold text-red-600">{{ project.name.error }}</span>
        </div>
        <div class="mt-2">
          <label
            for="scope"
            class="text-sm font-semibold"
          >GHG Emissions Scope</label>
          <select
            v-model="project.scope"
            class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300"
          >
            <option
              :value="null"
              disabled
            >
              Please select a scope
            </option>
            <option value="1">
              Scope 1
            </option>
            <option value="2">
              Scope 2
            </option>
            <option value="3">
              Scope 3
            </option>
          </select>
        </div>
        <div class="mt-2">
          <label
            for="reduction"
            class="flex justify-start"
          >
            <span class="text-sm font-semibold">Total Abatement Emission</span>
            <HelperText title="Total GHG emissions (tCO2e) abated over the life of the project. " />
          </label>
          <div class="relative">
            <input
              v-model="project.reduction.text"
              type="text"
              placeholder="Please enter your abate emission"
              class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300 placeholder:italic"
              @keyup="validatePositive('reduction')"
              @focusout="formatReduction"
            >
            <span class="absolute right-3 top-3 no-localize-translate">tCO2e</span>
          </div>
          <span class="text-xs font-semibold text-red-600">{{ project.reduction.error }}</span>
        </div>
        <div class="mt-2">
          <label
            for="startYear"
            class="flex justify"
          >
            <span class="text-sm font-semibold">Project Start Year</span>
            <HelperText title="The year which project is launched. " />
          </label>

          <DatePicker
            :value="project.startYear.text"
            minimum-view="year"
            :disabled-dates="state.disabledDates"
            input-class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300 placeholder:italic"
            required
            placeholder="Please select the start year of the project"
            :format="customFormatter"
            @input="project.startYear.text = $event"
            @selected="handleStartYearChanged"
          />
          <span class="text-xs font-semibold text-red-600">{{ project.startYear.error }}</span>
        </div>

        <div class="mt-2">
          <label
            for="implementedYear"
            class="flex justify-start"
          >
            <span class="text-sm font-semibold">Project Implemented Year</span>
            <HelperText title="The year which project start to take effect on GHG abatement emission." />
          </label>
          <DatePicker
            :value="project.implementedYear.text"
            minimum-view="year"
            input-class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300 placeholder:italic"
            required
            placeholder="Please select the implemented year of the project"
            :disabled-dates="state.disabledDates"
            :format="customFormatter"
            @input="project.implementedYear.text = $event"
            @selected="handleImplementedYearChanged"
          />
          <span class="text-xs font-semibold text-red-600">{{ project.implementedYear.error }}</span>
        </div>
        <div class="mt-2">
          <label
            for="endYear"
            class="flex justify-start"
          >
            <span class="text-sm font-semibold">Project End of Life Year</span>
            <HelperText title="The year which the project has completed it's GHG abatement lifespan." />
          </label>
          <DatePicker
            :value="project.endYear.text"
            minimum-view="year"
            input-class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300 placeholder:italic"
            required
            :disabled-dates="state.disabledDates"
            placeholder="Please select the end of life year of the project"
            :format="customFormatter"
            @input="project.endYear.text = $event"
            @selected="handleEndYearChanged"
          />
          <span class="text-xs font-semibold text-red-600">{{ project.endYear.error }}</span>
        </div>
        <div class="mt-2">
          <label
            for="cost"
            class="flex justify-start"
          >
            <span class="text-sm font-semibold">Capital Cost</span>
            <HelperText title="This is the initial cost of capital required in implementing a project." />
          </label>
          <div class="relative">
            <input
              v-model="project.cost.text"
              type="text"
              placeholder="Please enter the estimated cost of project"
              class="w-full px-6 py-3 border border-gray-300 outline-none focus:border-none focus:ring-2 focus:ring-emerald-300 placeholder:italic"
              @keyup="validatePositive('cost')"
              @focusout="formatCost"
            >
            <span class="absolute right-3 top-3">{{ currency }}</span>
          </div>
          <span class="text-xs font-semibold text-red-600">{{ project.cost.error }}</span>
        </div>
        <div class="flex items-center justify-end gap-2 mt-6 mb-4">
          <button
            type="button"
            aria-label="Cancel"
            class="px-4 py-2 text-sm border rounded outline-none border-slate-500 text-slate-900"
            @click="cancelProject"
          >
            Cancel
          </button>
          <button
            type="submit"
            aria-label="Save"
            class="brand-button"
            :disabled="cantSaveProject"
          >
            Save
          </button>
        </div>
      </form>
    </ModalComponent>
  </div>
</template>

<script>
export default {
  name: 'ManageAbatementButton',

  props: {
    abatementProjects: {
      type: Array,
      required: true,
    },
    companyCurrency: {
      type: String,
      required: true,
    },
  },

  emits: ['reloadProjects'],

  data() {
    return {
      project: {
        scope: null,
        name: {
          text: '',
          error: '',
        },
        reduction: {
          text: '',
          error: '',
        },
        startYear: {
          text: '',
          error: '',
        },
        implementedYear: {
          text: '',
          error: '',
        },
        endYear: {
          text: '',
          error: '',
        },
        cost: {
          text: '',
          error: '',
        },
      },
      currency: this.companyCurrency,
      showStartBubble: false,
      showImplementedBubble: false,
      showEndBubble: false,
      showCostBubble: false,
      showAbatementBubble: false,
      abatements: this.abatementProjects,
      state: {
        disabledDates: {
          from: new Date(2050, 0, 26),
        },
      },
    }
  },

  computed: {
    cantSaveProject() {
      const { name, reduction, cost, endYear, startYear, scope } = this.project
      return !scope || [startYear.text, endYear.text, cost.text, reduction.text, name.text].includes('') || [name.error, reduction.error, cost.error, startYear.error, endYear.error].some(error => error)
    },
  },

  watch: {
    abatementProjects(newProjects) {
      this.abatements = newProjects
    },
    companyCurrency(newCurrency) {
      this.currency = newCurrency
    },
  },

  methods: {
    handleStartYearChanged(date) {
      this.project.startYear.error = ''
      if (this.$moment(date).format('YYYY') > 2050) {
        this.project.startYear.error = 'The project start year cannot be beyond 2050'
        return
      }
      if (!this.project.endYear.text && !this.project.implementedYear.text) {
        return
      }
      this.validateEndYear()
    },
    handleImplementedYearChanged(date) {
      this.project.implementedYear.error = ''
      if (this.$moment(date).format('YYYY') > 2050) {
        this.project.implementedYear.error = 'The project implemented year cannot be beyond 2050'
        return
      }
      if (!this.project.endYear.text && !this.project.startYear.text) {
        return
      }
      this.validateEndYear()
    },
    handleEndYearChanged(date) {
      this.project.endYear.error = ''
      if (this.$moment(date).format('YYYY') > 2050) {
        this.project.endYear.error = 'The target completion year cannot be beyond 2050'
        return
      }
      if (!this.project.startYear.text && !this.project.implementedYear.text) {
        return
      }
      this.validateEndYear()
    },
    validateEndYear() {
      nextTick(() => {
        const { startYear, endYear, implementedYear } = this.project
        this.project.endYear.error = ''
        this.project.implementedYear.error = ''

        if (this.$moment(startYear.text).format('YYYY') > this.$moment(endYear.text).format('YYYY')) {
          this.project.endYear.error = 'The target year of completion cannot be prior to the start year'
        }
        if (this.$moment(implementedYear.text).format('YYYY') < this.$moment(startYear.text).format('YYYY')) {
          this.project.implementedYear.error = ''
          this.project.implementedYear.error = 'Project implemented year cannot be older than the project start year!'
        }

        if (this.$moment(endYear.text).format('YYYY') < this.$moment(implementedYear.text).format('YYYY')) {
          this.project.endYear.error = ''
          this.project.endYear.error = 'Project End of Life Year cannot be older than the Project Implemented Year!'
        }
      })
    },
    customFormatter(date) {
      return this.$moment(date).format('YYYY')
    },
    cancelProject() {
      this.project = {
        scope: null,
        name: {
          text: '',
          error: '',
        },
        reduction: {
          text: '',
          error: '',
        },
        startYear: {
          text: '',
          error: '',
        },
        implementedYear: {
          text: '',
          error: '',
        },
        endYear: {
          text: '',
          error: '',
        },
        cost: {
          text: '',
          error: '',
        },
      }
      this.$modal.hide('create-project')
    },

    createProject() {
      const { name, reduction, startYear, endYear, cost, scope, implementedYear } = this.project
      this.$apis.abatements
        .create({
          project_name: name.text,
          estimated_total_reduction: reduction.text,
          start_year: this.$moment(startYear.text).format('YYYY'),
          project_implemented_year: this.$moment(implementedYear.text).format('YYYY'),
          target_completion_year: this.$moment(endYear.text).format('YYYY'),
          estimated_cost: cost.text,
          company: localStorage.getItem('cid'),
          scope,
        })
        .then(() => {
          this.cancelProject()
          this.$toast('Your abatement project has been saved!', {
            timeout: 3000,
            position: 'top-right',
          })
          this.$emit('reloadProjects')
        })
    },
    validateName() {
      this.project.name.error = ''
      if (!/^[a-zA-Z0-9_ -./\]\\[]*$/.test(this.project.name.text)) {
        this.project.name.error = 'Please use A-Z, 0-9 and . [ ] / -_'
      }

      if (this.abatements.find(({ project_name }) => project_name.toLowerCase() === this.project.name.text.toLowerCase())) {
        this.project.name.error = 'Project with the same title already exists! Please key in another name'
      }
    },
    validatePositive(key) {
      this.project[key].error = ''
      if (parseFloat(this.project[key].text) < 1) {
        this.project[key].error = 'Please type in a positive number!'
      }
    },
    formatReduction() {
      this.project.reduction.text = parseFloat(this.project.reduction.text).toFixed(1)
    },
    formatCost() {
      this.project.cost.text = parseFloat(this.project.cost.text).toFixed(2)
    },
  },
}
</script>
